import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../constants';

export const JTI_MARKET_OPTIONS = [
  { id: 'uk', label: 'United Kingdom' },
  { id: 'italy', label: 'Italy' },
  { id: 'romania', label: 'Romania' },
  { id: 'czech', label: 'Czech Republic' },
  { id: 'poland', label: 'Poland' },
];

export const JTI_PROJECT_KEYS = ['jti', 'jti-global', 'jti-sweden'];

export function isJtiProjectKey(projectKey) {
  return JTI_PROJECT_KEYS.includes(String(projectKey || '').trim().toLowerCase());
}

export const MARKET_ANNOUNCEMENTS_UPDATED_EVENT = 'market-announcements-updated';

export const MARKET_COUNTRY_ALIASES = {
  uk: ['uk', 'united kingdom', 'gb', 'gbr', 'great britain', 'england'],
  italy: ['italy', 'it', 'ita', 'italia'],
  romania: ['romania', 'ro', 'rou'],
  germany: ['germany', 'de', 'deu', 'deutschland'],
  france: ['france', 'fr', 'fra', 'francia'],
  spain: ['spain', 'es', 'esp', 'espana'],
  poland: ['poland', 'pl', 'pol', 'polska'],
  czech: ['czech', 'cz', 'czech republic', 'ceska', 'cesko'],
  cz: ['czech', 'cz', 'czech republic', 'ceska', 'cesko'],
};

const DEFAULT_ANNOUNCEMENTS = [
  {
    market_id: 'italy',
    message: 'In Italy, sticks are not available to buy online; only indicative prices are shown.',
    enabled: true,
    category_scope: 'sticks',
  },
];

export const normalizeMarketValue = (value) => (value || '').toString().trim().toLowerCase();

export function countryMatchesMarket(marketId, countryValue) {
  const marketNorm = normalizeMarketValue(marketId);
  const countryNorm = normalizeMarketValue(countryValue);
  if (!marketNorm || !countryNorm) return false;
  if (marketNorm === countryNorm) return true;
  const aliases = MARKET_COUNTRY_ALIASES[marketNorm];
  if (aliases?.includes(countryNorm)) return true;
  return Object.entries(MARKET_COUNTRY_ALIASES).some(
    ([key, list]) =>
      (key === marketNorm || list.includes(marketNorm)) && list.includes(countryNorm)
  );
}

export function isAnnouncementMarketInScope(marketId, context) {
  const {
    isMarketScope,
    selectedMarket,
    selectedCountry,
    selectedCountries,
    groupBy,
    filteredDataRaw,
  } = context;

  if (isMarketScope) {
    return countryMatchesMarket(marketId, selectedMarket);
  }
  if (groupBy === 'countries' && Array.isArray(filteredDataRaw) && filteredDataRaw.length > 0) {
    return filteredDataRaw.some((row) => countryMatchesMarket(marketId, row?.country));
  }
  if (Array.isArray(selectedCountries) && selectedCountries.length > 0) {
    return selectedCountries.some((c) => countryMatchesMarket(marketId, c));
  }
  return countryMatchesMarket(marketId, selectedCountry);
}

export function announcementMatchesRoute(announcement, isStickRoute) {
  const scope = normalizeMarketValue(announcement?.category_scope || 'sticks');
  if (scope === 'all') return true;
  return scope === 'sticks' && isStickRoute;
}

/**
 * Resolve which configured announcements apply to current filters (same placement rules as legacy Italy sticks notice).
 */
export function resolveActiveMarketAnnouncements(announcements, context) {
  const list = Array.isArray(announcements) ? announcements : [];
  const active = list.filter(
    (item) =>
      item?.enabled !== false &&
      (item?.message || '').trim() &&
      announcementMatchesRoute(item, context.isStickRoute) &&
      isAnnouncementMarketInScope(item.market_id, context)
  );
  const messages = active.map((item) => String(item.message).trim());
  const hasNotice = messages.length > 0;
  const showAboveBoard = hasNotice && context.isHqScope && context.groupBy === 'countries';
  const showAboveChart =
    hasNotice && context.isHqScope && (context.selectedCountries?.length || 0) > 1;

  return {
    messages,
    hasNotice,
    showAboveBoard,
    showAboveChart,
  };
}

export async function fetchMarketAnnouncements() {
  const res = await axios.get(`${API_URL}/jti/market-announcements`);
  const list = res.data?.announcements;
  if (Array.isArray(list) && list.length > 0) return list;
  return DEFAULT_ANNOUNCEMENTS.map((item) => ({ ...item }));
}

export async function saveMarketAnnouncements(announcements) {
  const res = await axios.post(`${API_URL}/jti/market-announcements`, { announcements });
  window.dispatchEvent(new Event(MARKET_ANNOUNCEMENTS_UPDATED_EVENT));
  return res.data?.announcements || [];
}

export function useMarketAnnouncements() {
  const [announcements, setAnnouncements] = useState(DEFAULT_ANNOUNCEMENTS);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const list = await fetchMarketAnnouncements();
      setAnnouncements(list);
    } catch {
      setAnnouncements(DEFAULT_ANNOUNCEMENTS.map((item) => ({ ...item })));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const onUpdate = () => reload();
    window.addEventListener(MARKET_ANNOUNCEMENTS_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(MARKET_ANNOUNCEMENTS_UPDATED_EVENT, onUpdate);
  }, [reload]);

  return { announcements, loading, reload };
}

export function useResolvedMarketAnnouncements(context) {
  const { announcements } = useMarketAnnouncements();
  return useMemo(
    () => resolveActiveMarketAnnouncements(announcements, context),
    [announcements, context]
  );
}
